import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { useBalance } from "@/lib/balance-context";
import { capsules, billing } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import PageHead from "@/components/PageHead";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  UploadCloud,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import AICompose from "@/components/AICompose";

// ---------- Upload state machine ----------
// idle -> uploading -> verifying -> uploaded -> (user confirms) -> sealing
//                                 \-> verify_failed  (mismatch or preview cannot play)
const UPLOAD_STATES = Object.freeze({
  IDLE: "idle",
  UPLOADING: "uploading",
  VERIFYING: "verifying",
  UPLOADED: "uploaded",
  VERIFY_FAILED: "verify_failed",
});

const DURATION_TOLERANCE_SECONDS = 1.5; // browsers sometimes disagree by <1s on the same file
const MAX_VIDEO_BYTES = 95 * 1024 * 1024; // 95 MB — mirrors server-side MAX_VIDEO_BYTES, sized safely below the Cloudflare 100 MB edge cap
const MAX_VIDEO_LABEL = "95 MB";

const readLocalDuration = (file) =>
  new Promise((resolve) => {
    if (!file) return resolve(null);
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    const done = (val) => {
      URL.revokeObjectURL(url);
      resolve(val);
    };
    v.onloadedmetadata = () => {
      const d = v.duration;
      if (!Number.isFinite(d) || d <= 0) return done(null);
      done(d);
    };
    v.onerror = () => done(null);
    v.src = url;
  });

export default function CreateCapsule() {
  const { user } = useAuth();
  const { balance, refresh: refreshBalance } = useBalance();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const previewVideoRef = useRef(null);

  const [file, setFile] = useState(null);
  const [uploadState, setUploadState] = useState(UPLOAD_STATES.IDLE);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [pendingUpload, setPendingUpload] = useState(null); // {upload_id, size, content_type, filename}
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [localDuration, setLocalDuration] = useState(null);
  const [storedDuration, setStoredDuration] = useState(null);
  const [durationWarning, setDurationWarning] = useState(null);
  const [previewConfirmed, setPreviewConfirmed] = useState(false);

  const [busy, setBusy] = useState(false);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [buying, setBuying] = useState(false);
  const [form, setForm] = useState({
    title: "",
    message: "",
    sender_phone: user?.phone || "",
    recipient_name: "",
    recipient_email: "",
    recipient_phone: "",
    release_at: "",
    trigger_type: "scheduled",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const isMember = balance?.plan === "subscriber";
  const buyPrice = isMember ? "£1" : "£2.99";

  // ---------- Preview blob URL lifecycle ----------
  useEffect(() => {
    return () => {
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    };
  }, [previewBlobUrl]);

  // ---------- Reset on the sender re-picking a file ----------
  const resetUploadState = useCallback(
    (opts = {}) => {
      const { keepFile = false } = opts;
      setUploadState(UPLOAD_STATES.IDLE);
      setUploadPercent(0);
      setUploadError(null);
      setPendingUpload(null);
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
      setLocalDuration(null);
      setStoredDuration(null);
      setDurationWarning(null);
      setPreviewConfirmed(false);
      metadataLoadedRef.current = false;
      if (!keepFile) setFile(null);
    },
    [previewBlobUrl]
  );

  // ---------- The main upload flow ----------
  const startUpload = useCallback(
    async (picked) => {
      setFile(picked);
      setUploadState(UPLOAD_STATES.UPLOADING);
      setUploadPercent(0);
      setUploadError(null);
      setPendingUpload(null);
      setPreviewConfirmed(false);
      setStoredDuration(null);
      setDurationWarning(null);

      // Grab local duration in parallel; skip if the browser can't decode metadata.
      readLocalDuration(picked).then(setLocalDuration);

      try {
        const resp = await capsules.uploadVideo(picked, {
          onProgress: (pct) => setUploadPercent(pct),
        });
        // Belt-and-braces client-side size check (server already enforces this).
        if (resp?.size !== picked.size) {
          setUploadError(
            `Uploaded byte count (${resp?.size}) does not match the selected file (${picked.size}). Please try again.`
          );
          setUploadState(UPLOAD_STATES.VERIFY_FAILED);
          return;
        }
        setPendingUpload(resp);
        setUploadState(UPLOAD_STATES.VERIFYING);

        // Fetch the STORED bytes back to build the preview player.
        try {
          const blob = await capsules.pendingVideoBlob(resp.upload_id);
          if (blob.size !== picked.size) {
            setUploadError(
              `Playback stream size (${blob.size}) does not match the uploaded file (${picked.size}). Please try again.`
            );
            setUploadState(UPLOAD_STATES.VERIFY_FAILED);
            return;
          }
          const url = URL.createObjectURL(blob);
          setPreviewBlobUrl(url);
          setUploadState(UPLOAD_STATES.UPLOADED);
        } catch (fetchErr) {
          setUploadError(
            "Upload could not be verified — the stored video could not be played back. Please try again."
          );
          setUploadState(UPLOAD_STATES.VERIFY_FAILED);
        }
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const msg =
          (detail && typeof detail === "object" && detail.message) ||
          (typeof detail === "string" ? detail : null) ||
          "Upload failed. Please try again.";
        setUploadError(msg);
        setUploadState(UPLOAD_STATES.VERIFY_FAILED);
      }
    },
    []
  );

  const onPickFile = (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    // Client-side hard limit — reject before any bytes hit the network. Backend
    // enforces the same 95 MB cap independently as a safety net.
    if (picked.size > MAX_VIDEO_BYTES) {
      resetUploadState();
      setFile(picked);
      setUploadError(
        "This video is larger than 95 MB. Please choose a smaller file or reduce the video quality before uploading."
      );
      setUploadState(UPLOAD_STATES.VERIFY_FAILED);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    // If there's an existing pending upload on the server, best-effort delete it so we
    // don't leave the sender's account cluttered with orphaned pending blobs.
    if (pendingUpload?.upload_id) {
      capsules
        .deletePendingUpload(pendingUpload.upload_id)
        .catch(() => {
          /* best-effort — never block the user */
        });
    }
    resetUploadState({ keepFile: false });
    startUpload(picked);
  };

  const removeUpload = async () => {
    if (pendingUpload?.upload_id) {
      try {
        await capsules.deletePendingUpload(pendingUpload.upload_id);
      } catch {
        /* best-effort */
      }
    }
    resetUploadState();
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ---------- Duration cross-check when the preview element reports metadata ----------
  const metadataLoadedRef = useRef(false);

  const onPreviewMetadata = () => {
    const v = previewVideoRef.current;
    if (!v) return;
    metadataLoadedRef.current = true;
    const d = v.duration;
    if (!Number.isFinite(d) || d <= 0) {
      setStoredDuration(null);
      return;
    }
    setStoredDuration(d);
    if (localDuration && Math.abs(d - localDuration) > DURATION_TOLERANCE_SECONDS) {
      setDurationWarning(
        `The uploaded video appears shorter than the file you selected (uploaded ${d.toFixed(
          1
        )}s vs original ${localDuration.toFixed(1)}s). Please re-upload before sealing.`
      );
    } else {
      setDurationWarning(null);
    }
  };

  const onPreviewError = () => {
    // Ignore transient errors that fire AFTER metadata loaded successfully — at that
    // point the browser has already decoded enough of the file to prove it is a valid,
    // playable video. Late errors (network hiccup on further chunks, muted-audio quirks
    // on some codecs, autoplay-policy denials) must not invalidate a good upload.
    if (metadataLoadedRef.current) return;
    const v = previewVideoRef.current;
    const code = v?.error?.code;
    // Only MEDIA_ERR_DECODE (3) and MEDIA_ERR_SRC_NOT_SUPPORTED (4) definitively mean
    // the file is not playable. Aborted (1) and network (2) can be transient.
    if (code === 3 || code === 4) {
      setUploadError(
        "Upload could not be verified — the stored video is not a playable file. Please try again."
      );
      setUploadState(UPLOAD_STATES.VERIFY_FAILED);
    }
  };

  const startTopUp = async () => {
    setBuying(true);
    try {
      const { checkout_url } = await billing.checkoutMessage();
      window.location.href = checkout_url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start checkout");
      setBuying(false);
    }
  };

  // ---------- Seal button gating ----------
  const canSeal =
    uploadState === UPLOAD_STATES.UPLOADED &&
    !!pendingUpload?.upload_id &&
    previewConfirmed &&
    !durationWarning &&
    !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (uploadState !== UPLOAD_STATES.UPLOADED || !pendingUpload?.upload_id) {
      toast.error("Please upload and preview your video first.");
      return;
    }
    if (!previewConfirmed) {
      toast.error("Please confirm you have previewed the uploaded video.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      const iso = new Date(form.release_at).toISOString();
      fd.set("release_at", iso);
      fd.append("upload_id", pendingUpload.upload_id);
      const created = await capsules.create(fd);
      toast.success("Capsule sealed");
      refreshBalance();
      navigate(`/capsules/${created.id}`);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 402 && detail?.code === "no_credits") {
        setUpsellOpen(true);
      } else if (detail?.code === "upload_not_found" || detail?.code === "upload_already_used") {
        toast.error(detail.message || "Please re-upload your video and try again.");
        resetUploadState();
      } else {
        toast.error(typeof detail === "string" ? detail : detail?.message || "Failed to seal capsule");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHead title="Seal a new capsule | ChronoVaults" noindex />
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-12">
      <p className="label-mono text-terracotta">Compose</p>
      <h1 className="font-serif text-5xl text-bone mt-3 leading-none">Seal a new capsule.</h1>
      <p className="text-white/50 mt-3 max-w-xl text-sm">
        Every field below is engraved into the capsule. It cannot be changed once sealed.
      </p>

      <form onSubmit={submit} className="mt-12 grid lg:grid-cols-5 gap-10" data-testid="capsule-form">
        <div className="lg:col-span-3 space-y-8">
          <section>
            <p className="label-mono mb-4">01 · The Message</p>
            <div className="space-y-5">
              <div>
                <Label className="label-mono mb-3 block">Title</Label>
                <Input
                  required
                  value={form.title}
                  onChange={set("title")}
                  placeholder="For Amelia, on her 18th"
                  data-testid="capsule-title"
                  className="h-12"
                />
              </div>
              <div>
                <Label className="label-mono mb-3 block">Note (optional)</Label>
                <Textarea
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  data-testid="capsule-message"
                  placeholder="A short note to accompany the video..."
                />
              </div>
            </div>
          </section>

          <section>
            <p className="label-mono mb-4">02 · Recipient</p>
            <div className="grid grid-cols-2 gap-5">
              <div className="col-span-2">
                <Label className="label-mono mb-3 block">Name</Label>
                <Input required value={form.recipient_name} onChange={set("recipient_name")} data-testid="rcp-name" className="h-12" />
              </div>
              <div className="col-span-2">
                <Label className="label-mono mb-3 block">Email</Label>
                <Input type="email" required value={form.recipient_email} onChange={set("recipient_email")} data-testid="rcp-email" className="h-12" />
              </div>
              <div className="col-span-2">
                <Label className="label-mono mb-3 block">Phone (recipient will verify with this)</Label>
                <Input required placeholder="+15551234567" value={form.recipient_phone} onChange={set("recipient_phone")} data-testid="rcp-phone" className="h-12" />
              </div>
            </div>
          </section>

          <section>
            <p className="label-mono mb-4">03 · Sender Identity</p>
            <div>
              <Label className="label-mono mb-3 block">Your phone (recipient will match this)</Label>
              <Input required placeholder="+15551234567" value={form.sender_phone} onChange={set("sender_phone")} data-testid="sender-phone" className="h-12" />
            </div>
          </section>

          <section>
            <p className="label-mono mb-4">04 · Release Event</p>
            <div>
              <Label className="label-mono mb-3 block">Scheduled release time</Label>
              <Input
                type="datetime-local"
                required
                value={form.release_at}
                onChange={set("release_at")}
                data-testid="release-at"
                className="h-12"
              />
              <p className="text-xs text-white/40 mt-3">
                External / webhook triggers can be added later — this MVP supports scheduled release + manual override.
              </p>
            </div>
          </section>
        </div>

        {/* Right column: video upload + preview + confirm */}
        <div className="lg:col-span-2 space-y-6">
          <AICompose
            title={form.title}
            message={form.message}
            occasion={form.title}
            recipientName={form.recipient_name}
            onTitle={(t) => setForm((f) => ({ ...f, title: t }))}
            onMessage={(m) => setForm((f) => ({ ...f, message: m }))}
          />

          <p className="label-mono">05 · The Video</p>

          {/* --- Drop / picker --- */}
          <div
            className={`border border-dashed p-8 transition-colors ${
              uploadState === UPLOAD_STATES.IDLE
                ? "border-white/15 cursor-pointer hover:border-terracotta/50"
                : "border-white/10"
            }`}
            onClick={() => {
              if (uploadState === UPLOAD_STATES.IDLE) fileInputRef.current?.click();
            }}
            data-testid="video-drop"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              hidden
              onChange={onPickFile}
              data-testid="video-input"
            />
            {uploadState === UPLOAD_STATES.IDLE && !file && (
              <div className="text-center">
                <UploadCloud className="mx-auto w-8 h-8 text-white/50 mb-4" strokeWidth={1.2} />
                <p className="font-serif text-xl text-bone">Attach your video</p>
                <p className="text-xs text-white/50 mt-2">Any format · up to {MAX_VIDEO_LABEL}</p>
              </div>
            )}

            {(uploadState === UPLOAD_STATES.UPLOADING ||
              uploadState === UPLOAD_STATES.VERIFYING) &&
              file && (
                <div className="text-center" data-testid="upload-progress">
                  <Loader2 className="mx-auto w-8 h-8 text-terracotta mb-4 animate-spin" strokeWidth={1.5} />
                  <p className="font-serif text-xl text-bone">{file.name}</p>
                  <p className="text-xs text-white/50 mt-2">
                    {(file.size / 1048576).toFixed(1)} MB
                  </p>
                  <p className="label-mono text-terracotta mt-5" data-testid="upload-status-label">
                    {uploadState === UPLOAD_STATES.UPLOADING
                      ? `Uploading… ${uploadPercent}%`
                      : "Verifying upload…"}
                  </p>
                  <div className="mt-4">
                    <Progress
                      value={uploadState === UPLOAD_STATES.UPLOADING ? uploadPercent : 100}
                      className="h-1"
                    />
                  </div>
                </div>
              )}

            {uploadState === UPLOAD_STATES.UPLOADED && file && (
              <div className="text-center" data-testid="upload-complete">
                <CheckCircle2 className="mx-auto w-8 h-8 text-terracotta mb-4" strokeWidth={1.5} />
                <p className="font-serif text-xl text-bone">{file.name}</p>
                <p className="text-xs text-white/50 mt-2">
                  {(pendingUpload?.size / 1048576).toFixed(1)} MB · Upload complete
                </p>
              </div>
            )}

            {uploadState === UPLOAD_STATES.VERIFY_FAILED && file && (
              <div className="text-center" data-testid="upload-failed">
                <AlertTriangle className="mx-auto w-8 h-8 text-terracotta mb-4" strokeWidth={1.5} />
                <p className="font-serif text-xl text-bone">{file.name}</p>
                <p className="text-xs text-white/50 mt-2">Upload could not be verified</p>
              </div>
            )}
          </div>

          {/* Persistent size-limit hint — visible before a file is chosen so the user
              never has to guess what's allowed. Backend enforces the same cap. */}
          {uploadState === UPLOAD_STATES.IDLE && (
            <p
              className="text-xs text-white/50 leading-relaxed mt-3"
              data-testid="upload-size-hint"
            >
              Upload a video up to {MAX_VIDEO_LABEL}. You&rsquo;ll be able to preview the exact
              uploaded version before sealing your ChronoVault.
            </p>
          )}

          {/* --- Preview player (only when the STORED bytes are available) --- */}
          {uploadState === UPLOAD_STATES.UPLOADED && previewBlobUrl && (
            <div className="border border-white/10 bg-vault-950" data-testid="upload-preview">
              <p className="label-mono text-terracotta px-4 pt-4">Preview your uploaded video</p>
              <video
                ref={previewVideoRef}
                controls
                preload="metadata"
                src={previewBlobUrl}
                onLoadedMetadata={onPreviewMetadata}
                onError={onPreviewError}
                className="w-full mt-3 bg-black max-h-80"
                data-testid="upload-preview-video"
              />
              <div className="px-4 pb-4 pt-3 space-y-2">
                <p className="text-xs text-white/50" data-testid="upload-preview-meta">
                  Stored size: {(pendingUpload?.size / 1048576).toFixed(2)} MB
                  {storedDuration ? ` · ${storedDuration.toFixed(1)}s` : ""}
                  {localDuration ? ` · original ${localDuration.toFixed(1)}s` : ""}
                </p>
                {durationWarning && (
                  <div
                    className="flex items-start gap-2 text-xs text-terracotta bg-terracotta/10 border border-terracotta/30 p-3"
                    data-testid="upload-duration-warning"
                    role="alert"
                  >
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span>{durationWarning}</span>
                  </div>
                )}
              </div>

              <div className="flex items-start gap-3 border-t border-white/5 px-4 py-4">
                <Checkbox
                  id="upload-confirm"
                  checked={previewConfirmed}
                  onCheckedChange={(v) => setPreviewConfirmed(v === true)}
                  disabled={!!durationWarning}
                  data-testid="upload-confirm-checkbox"
                  className="mt-1"
                />
                <Label
                  htmlFor="upload-confirm"
                  className="text-xs text-white/70 leading-relaxed cursor-pointer"
                >
                  I have previewed this video and confirm it uploaded correctly.
                </Label>
              </div>
            </div>
          )}

          {/* --- Verify failed panel --- */}
          {uploadState === UPLOAD_STATES.VERIFY_FAILED && (
            <div
              className="border border-terracotta/40 bg-terracotta/5 p-4 space-y-3"
              data-testid="upload-failed-panel"
              role="alert"
            >
              <div className="flex items-start gap-2 text-sm text-bone">
                <AlertTriangle className="w-4 h-4 mt-0.5 text-terracotta flex-shrink-0" strokeWidth={1.5} />
                <span>{uploadError || "Upload could not be verified — please try again."}</span>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={removeUpload}
                data-testid="upload-retry-btn"
                className="h-9 border-white/20 bg-transparent text-bone hover:bg-white/5 rounded-sm text-xs"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Remove &amp; try again
              </Button>
            </div>
          )}

          {/* --- Remove button when upload is complete --- */}
          {uploadState === UPLOAD_STATES.UPLOADED && (
            <Button
              type="button"
              variant="ghost"
              onClick={removeUpload}
              data-testid="upload-remove-btn"
              className="text-xs text-white/50 hover:text-bone"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Remove &amp; re-upload
            </Button>
          )}

          <div className="mt-6 p-5 bg-vault-800 border border-white/5">
            <p className="label-mono">Capsule preview</p>
            <p className="font-serif text-2xl text-bone mt-3 leading-tight">{form.title || "Untitled capsule"}</p>
            <p className="text-xs text-white/50 mt-2">To {form.recipient_name || "—"}</p>
            <p className="text-xs text-terracotta mt-4">
              Releases: {form.release_at ? new Date(form.release_at).toLocaleString() : "not set"}
            </p>
          </div>

          <Button
            type="submit"
            disabled={!canSeal}
            data-testid="seal-btn"
            className="mt-8 w-full h-14 bg-terracotta hover:bg-terracotta/90 text-white rounded-sm tracking-widest text-xs uppercase disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "Sealing..." : "Seal capsule"}
          </Button>
          {!canSeal && uploadState === UPLOAD_STATES.UPLOADED && !previewConfirmed && (
            <p className="text-xs text-white/40 text-center -mt-4" data-testid="seal-gate-hint">
              Preview the video and confirm the checkbox above to enable sealing.
            </p>
          )}
          {uploadState === UPLOAD_STATES.IDLE && (
            <p className="text-xs text-white/40 text-center -mt-4" data-testid="seal-gate-hint">
              Upload a video to enable sealing.
            </p>
          )}
        </div>
      </form>

      <Dialog open={upsellOpen} onOpenChange={setUpsellOpen}>
        <DialogContent data-testid="no-credits-upsell" className="bg-vault-950 border border-white/10 text-bone">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">You've used all your credits.</DialogTitle>
            <DialogDescription className="text-white/60 text-sm mt-2">
              Top up one message for {buyPrice} and continue sealing this capsule. Your form values are preserved.
              {!isMember && " Members pay just £1/message — see membership below."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
            <Button
              variant="outline"
              onClick={() => navigate("/pricing")}
              data-testid="upsell-view-pricing"
              className="h-11 border-white/20 bg-transparent text-bone hover:bg-white/5 rounded-sm"
            >
              View all plans
            </Button>
            <Button
              onClick={startTopUp}
              disabled={buying}
              data-testid="upsell-buy-message"
              className="h-11 bg-terracotta hover:bg-terracotta/90 text-white rounded-sm"
            >
              {buying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Redirecting…
                </>
              ) : (
                `Buy 1 message · ${buyPrice}`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
}
